import { Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SocialContactField, WebsiteContactField } from '@/components/ui/social-contact-field'
import {
  EMPTY_EXTRA_SOCIAL_FIELDS,
  OPTIONAL_SOCIAL_NETWORKS,
  extraSocialContactForEdit,
  normalizeExtraSocialOrder,
  SOCIAL_FIELD_BY_NETWORK,
  SOCIAL_NETWORK_LABEL,
  type ExtraSocialContact,
  type ExtraSocialSource,
  type SocialContactNetwork,
} from '@/lib/social-networks'

export type EntityPublicContactValue = {
  website: string
  linkedin_url: string
  x_url: string
} & ExtraSocialContact

export function emptyEntityPublicContactValue(): EntityPublicContactValue {
  return {
    website: '',
    linkedin_url: '',
    x_url: '',
    ...EMPTY_EXTRA_SOCIAL_FIELDS,
    extra_social_order: [],
  }
}

export function entityPublicContactFrom(
  source:
    | (ExtraSocialSource & {
        website?: string | null
        linkedin_url?: string | null
        x_url?: string | null
      })
    | null
    | undefined,
): EntityPublicContactValue {
  const extras = extraSocialContactForEdit(source)
  return {
    website: source?.website ?? '',
    linkedin_url: source?.linkedin_url ?? '',
    x_url: source?.x_url ?? '',
    ...extras,
  }
}

type EntityPublicContactFieldsProps = {
  value: EntityPublicContactValue
  onChange: (patch: Partial<EntityPublicContactValue>) => void
  idPrefix?: string
  disabled?: boolean
}

/** Website always on; socials via Add social in the order they were added. */
export function EntityPublicContactFields({
  value,
  onChange,
  idPrefix = 'entity-public-contact',
  disabled = false,
}: EntityPublicContactFieldsProps) {
  const visibleSocials = normalizeExtraSocialOrder(value.extra_social_order)
  const remaining = OPTIONAL_SOCIAL_NETWORKS.filter((network) => !visibleSocials.includes(network))

  const handleAdd = (network: SocialContactNetwork) => {
    onChange({ extra_social_order: [...visibleSocials, network] })
  }

  const handleRemove = (network: SocialContactNetwork) => {
    const key = SOCIAL_FIELD_BY_NETWORK[network] as keyof EntityPublicContactValue
    onChange({
      [key]: '',
      extra_social_order: visibleSocials.filter((item) => item !== network),
    })
  }

  return (
    <>
      <WebsiteContactField
        id={`${idPrefix}-website`}
        value={value.website}
        onChange={(website) => onChange({ website })}
        disabled={disabled}
      />
      {visibleSocials.map((network) => {
        const key = SOCIAL_FIELD_BY_NETWORK[network] as keyof EntityPublicContactValue
        return (
          <div key={network} className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <SocialContactField
                network={network}
                id={`${idPrefix}-${network}`}
                value={String(value[key] ?? '')}
                onChange={(nextValue) => onChange({ [key]: nextValue })}
                disabled={disabled}
              />
            </div>
            <Button
              type="button"
              variant="ghostMuted"
              size="iconSm"
              aria-label={`Remove ${SOCIAL_NETWORK_LABEL[network]}`}
              disabled={disabled}
              onClick={() => handleRemove(network)}
            >
              <X />
            </Button>
          </div>
        )
      })}
      {remaining.length > 0 ? (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghostMuted" size="sm" disabled={disabled}>
              <Plus />
              Add social
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {remaining.map((network) => (
              <DropdownMenuItem key={network} onSelect={() => handleAdd(network)}>
                {SOCIAL_NETWORK_LABEL[network]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </>
  )
}
